import type { InferRouterOutputs } from "@orpc/server";
import type { DeletionStatusName } from "@repo/api/modules/privacy/types";
import type { ApiRouter } from "@repo/api/orpc/router";
import { orpc } from "@shared/lib/orpc";

type Outputs = InferRouterOutputs<ApiRouter>["privacy"];
export type PrivacySettingsData = Outputs["settings"];
export type DeletionRow = Outputs["deletions"]["list"]["requests"][number];

export const privacySettingsQuery = (organizationId: string) =>
	orpc.privacy.settings.queryOptions({ input: { organizationId } });

/** Polls every 2 s while a request is still running, so its counts appear without a reload. */
export const deletionsQuery = (organizationId: string) =>
	orpc.privacy.deletions.list.queryOptions({
		input: { organizationId },
		refetchInterval: (query) =>
			query.state.data?.requests.some(
				(r) => r.status === "PENDING" || r.status === "RUNNING",
			)
				? 2_000
				: false,
	});

export const STATUS_BADGE: Record<
	DeletionStatusName,
	{ label: string; variant: "info" | "success" | "destructive" }
> = {
	PENDING: { label: "Queued", variant: "info" },
	RUNNING: { label: "Deleting", variant: "info" },
	DONE: { label: "Deleted", variant: "success" },
	FAILED: { label: "Failed", variant: "destructive" },
};

/** Where a request came from, as the screen names it. */
export const VIA_LABEL: Record<DeletionRow["requestedVia"], string> = {
	api: "API",
	admin: "Admin",
};

/** "1 conversation", "1,200 conversations". */
export const plural = (n: number, one: string, many = `${one}s`) =>
	`${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/** "3 conversations, 41 messages, 1 order anonymised", what is still to come, or why it failed. */
export function removedSummary(row: DeletionRow): string {
	if (row.status === "FAILED" && row.error) {
		return row.error;
	}
	const counts = row.counts;
	if (!counts) {
		return {
			PENDING: "Not started yet",
			RUNNING: "In progress",
			DONE: "Nothing stored",
			FAILED: "Nothing removed",
		}[row.status];
	}
	const parts = [
		counts.conversations
			? plural(counts.conversations, "conversation")
			: null,
		counts.messages ? plural(counts.messages, "message") : null,
		counts.ordersAnonymised
			? `${plural(counts.ordersAnonymised, "order")} anonymised`
			: null,
		counts.shopperProfiles ? "saved memory" : null,
		counts.shopperSessions
			? plural(counts.shopperSessions, "session")
			: null,
	].filter(Boolean);
	const summary = parts.length
		? parts.join(", ")
		: row.status === "DONE"
			? "Nothing was stored for this id"
			: "Nothing yet";
	// Traces are best effort: say so when they could not be reached.
	return counts.traces === null ? `${summary}; traces not reached` : summary;
}

/** What saving the retention period means, in the words of the setting. */
export const retentionEffect = (days: number) =>
	`Conversations older than ${days.toLocaleString("en-US")} days are deleted nightly.`;
